import { StrictMode, useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  FingerprintCapture,
  FingerprintCaptureError,
  SDK_VERSION,
  type FingerType,
} from "../src/react";
import "./style.css";

const fingerLabels: Record<FingerType, string> = {
  unspecified: "Any finger",
  right_four_fingers: "Right hand - 4 fingers",
  right_thumb: "Right thumb",
  right_index: "Right index",
  right_middle: "Right middle",
  right_ring: "Right ring",
  right_little: "Right little",
  left_four_fingers: "Left hand - 4 fingers",
  left_thumb: "Left thumb",
  left_index: "Left index",
  left_middle: "Left middle",
  left_ring: "Left ring",
  left_little: "Left little",
};

const fingerGroups: Array<{
  label: string;
  fingers: FingerType[];
}> = [
  {
    label: "Right hand",
    fingers: [
      "right_four_fingers",
      "right_thumb",
      "right_index",
      "right_middle",
      "right_ring",
      "right_little",
    ],
  },
  {
    label: "Left hand",
    fingers: [
      "left_four_fingers",
      "left_thumb",
      "left_index",
      "left_middle",
      "left_ring",
      "left_little",
    ],
  },
];

function Demo() {
  const [finger, setFinger] = useState<FingerType>("right_thumb");
  const [image, setImage] = useState<Blob | null>(null);
  const [error, setError] = useState<FingerprintCaptureError | null>(null);
  const [fingerMenuOpen, setFingerMenuOpen] = useState(false);
  const fingerPickerRef = useRef<HTMLDivElement>(null);
  const imageUrl = useMemo(
    () => (image ? URL.createObjectURL(image) : null),
    [image],
  );

  useEffect(
    () => () => {
      if (imageUrl) URL.revokeObjectURL(imageUrl);
    },
    [imageUrl],
  );

  useEffect(() => {
    const closeOnOutsideClick = (event: PointerEvent) => {
      if (
        event.target instanceof Node &&
        !fingerPickerRef.current?.contains(event.target)
      ) {
        setFingerMenuOpen(false);
      }
    };

    document.addEventListener("pointerdown", closeOnOutsideClick);
    return () => document.removeEventListener("pointerdown", closeOnOutsideClick);
  }, []);

  const handleComplete = (blob: Blob) => {
    setImage(blob);
    setError(null);
  };

  const download = () => {
    if (!imageUrl) return;
    const link = document.createElement("a");
    link.href = imageUrl;
    link.download = `${finger}-${Date.now()}.png`;
    link.click();
  };

  return (
    <main>
      <nav className="nav">
        <a className="brand" href="/" aria-label="Data Mint home">
          <span className="brand-mark">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 3a9 9 0 0 0-9 9m18 0a9 9 0 0 0-9-9m-6 9a6 6 0 0 1 12 0m-9 0a3 3 0 0 1 6 0m-8.7 4.7A8 8 0 0 0 12 18a8 8 0 0 0 5.7-2.3M9.2 21a12 12 0 0 0 5.6 0" />
            </svg>
          </span>
          <span>Data Mint <em>Labs</em></span>
        </a>
        <span className="version">SDK v{SDK_VERSION}</span>
      </nav>

      <section className="hero">
        <div className="hero-copy">
          <div className="pill">
            <span />
            100% on-device processing
          </div>
          <h1>
            A clear fingerprint,
            <span> straight from the camera.</span>
          </h1>
          <p className="lead">
            Test the touchless web SDK. It guides placement, waits for a clear
            and stable view, then returns the natural full-color camera frame
            to your application.
          </p>

          <div className="controls">
            <div className="finger-field">
              <span className="field-label" id="finger-label">
                Finger to capture
              </span>
              <div
                className="finger-picker"
                ref={fingerPickerRef}
                onKeyDown={(event) => {
                  if (event.key === "Escape") setFingerMenuOpen(false);
                }}
              >
                <button
                  className="finger-trigger"
                  type="button"
                  aria-haspopup="listbox"
                  aria-expanded={fingerMenuOpen}
                  aria-labelledby="finger-label finger-value"
                  onClick={() => setFingerMenuOpen((open) => !open)}
                >
                  <span id="finger-value">{fingerLabels[finger]}</span>
                </button>
                {fingerMenuOpen && (
                  <div className="finger-menu" role="listbox" aria-label="Finger">
                    <button
                      className="finger-option"
                      type="button"
                      role="option"
                      aria-selected={finger === "unspecified"}
                      onClick={() => {
                        setFinger("unspecified");
                        setFingerMenuOpen(false);
                      }}
                    >
                      {fingerLabels.unspecified}
                    </button>
                    {fingerGroups.map((group) => (
                      <div
                        className="finger-group"
                        role="group"
                        aria-label={group.label}
                        key={group.label}
                      >
                        <div className="finger-group-label">{group.label}</div>
                        {group.fingers.map((value) => (
                          <button
                            className="finger-option"
                            type="button"
                            role="option"
                            aria-selected={finger === value}
                            key={value}
                            onClick={() => {
                              setFinger(value);
                              setFingerMenuOpen(false);
                            }}
                          >
                            {fingerLabels[value]}
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <FingerprintCapture
              options={{
                finger,
                debug: true,
                timeoutMs: 60_000,
                theme: { accentColor: "#55e6a5" },
              }}
              onComplete={handleComplete}
              onError={setError}
              className="capture-button"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 3a9 9 0 0 0-9 9m18 0a9 9 0 0 0-9-9m-6 9a6 6 0 0 1 12 0m-9 0a3 3 0 0 1 6 0m-8.7 4.7A8 8 0 0 0 12 18a8 8 0 0 0 5.7-2.3M9.2 21a12 12 0 0 0 5.6 0" />
              </svg>
              Start camera test
            </FingerprintCapture>
          </div>

          {error && (
            <div className="error" role="alert">
              <strong>{error.code.replaceAll("_", " ")}</strong>
              <span>{error.message}</span>
            </div>
          )}

          <div className="trust-row">
            <span>No upload</span>
            <span>No third-party API</span>
            <span>Camera closes automatically</span>
          </div>
        </div>

        <div className="preview-card">
          <div className="preview-top">
            <div>
              <span className="kicker">SDK output</span>
              <h2>{image ? "Capture complete" : "Waiting for capture"}</h2>
            </div>
            <span className={`status ${image ? "complete" : ""}`}>
              <i />
              {image ? "Ready" : "Idle"}
            </span>
          </div>

          <div className={`result-stage ${image ? "has-result" : ""}`}>
            {imageUrl ? (
              <img src={imageUrl} alt="Full-color fingerprint camera capture" />
            ) : (
              <div className="placeholder">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 3a9 9 0 0 0-9 9m18 0a9 9 0 0 0-9-9m-6 9a6 6 0 0 1 12 0m-9 0a3 3 0 0 1 6 0m-8.7 4.7A8 8 0 0 0 12 18a8 8 0 0 0 5.7-2.3M9.2 21a12 12 0 0 0 5.6 0" />
                </svg>
                <span>The full-color camera frame will appear here</span>
              </div>
            )}
          </div>

          <div className="preview-footer">
            <div>
              <span>Output</span>
              <strong>Full-color PNG</strong>
            </div>
            <div>
              <span>Capture</span>
              <strong>On-device</strong>
            </div>
            <button type="button" onClick={download} disabled={!image}>
              Download
            </button>
          </div>
        </div>
      </section>

      <section className="steps">
        <div>
          <span className="step-number">01</span>
          <strong>Place</strong>
          <p>Align one finger with the real-time guide.</p>
        </div>
        <div>
          <span className="step-number">02</span>
          <strong>Hold</strong>
          <p>The SDK waits for focus, lighting, and stability.</p>
        </div>
        <div>
          <span className="step-number">03</span>
          <strong>Receive</strong>
          <p>Your app gets the accepted full-color camera frame as a PNG Blob.</p>
        </div>
      </section>
    </main>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Demo />
  </StrictMode>,
);
